import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Brand slide transition: the one easing curve at the indicator-slide duration.
 * Falls back to literal values if the CSS custom properties are unavailable.
 */
const SLIDE_TRANSITION =
  'transform var(--dur-slide, 0.5s) var(--ease, cubic-bezier(0.16, 1, 0.3, 1)),' +
  ' width var(--dur-slide, 0.5s) var(--ease, cubic-bezier(0.16, 1, 0.3, 1))';

/** Measured horizontal offset and width of the active tab. */
interface UnderlineMetrics {
  /** offsetLeft of the active tab within the positioned list container, in px. */
  left: number;
  /** offsetWidth of the active tab, in px. */
  width: number;
}

/** What {@link useSlidingUnderline} hands back to a tab list. */
export interface SlidingUnderlineResult {
  /** Attach to the relative TabList container so the underline can be measured/observed. */
  listRef: React.RefObject<HTMLDivElement | null>;
  /** Register the tab button element at `index` (pass null on unmount). */
  setTabRef: (index: number) => (el: HTMLElement | null) => void;
  /** Inline style for the single underline indicator (transform + width + slide easing). */
  indicatorStyle: React.CSSProperties;
}

/**
 * Drives a single underline indicator that physically slides under the selected
 * tab via `transform: translateX`, rather than repainting per-tab borders.
 *
 * Measures the selected tab's `offsetLeft`/`offsetWidth` (relative to the
 * positioned list container) in a layout effect on `selectedIndex`/`count`
 * change, and re-measures via a ResizeObserver on the list for responsiveness.
 * Pre-layout/SSR measurements (0 width, as in jsdom) are ignored so the
 * indicator stays collapsed and nothing crashes before the first real layout.
 *
 * @param selectedIndex Index of the currently selected tab.
 * @param count Number of tabs (re-measure when tabs are added/removed).
 * @returns Refs to wire up and the inline style for the indicator element.
 */
export function useSlidingUnderline(selectedIndex: number, count: number): SlidingUnderlineResult {
  const listRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const [metrics, setMetrics] = useState<UnderlineMetrics>({ left: 0, width: 0 });

  const setTabRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      tabRefs.current[index] = el;
    },
    [],
  );

  const measure = useCallback(() => {
    const el = tabRefs.current[selectedIndex];
    if (!el) return;
    const width = el.offsetWidth;
    const left = el.offsetLeft;
    // Guard against pre-layout/SSR/jsdom values (0 or NaN): keep the indicator
    // collapsed until a real measurement lands so it never jumps from origin.
    if (!Number.isFinite(width) || !Number.isFinite(left) || width <= 0) return;
    setMetrics((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
  }, [selectedIndex]);

  // Re-measure on selection change and when the tab set changes.
  useLayoutEffect(() => {
    measure();
  }, [measure, count]);

  // Re-measure responsively as the list resizes (font load, container reflow).
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(list);
    return () => observer.disconnect();
  }, [measure]);

  const indicatorStyle: React.CSSProperties = {
    width: metrics.width,
    transform: `translateX(${metrics.left}px)`,
    transition: SLIDE_TRANSITION,
  };

  return { listRef, setTabRef, indicatorStyle };
}
